import { useState } from "react";
import { ActionIcon, Badge, Button, Card, Checkbox, Group, NumberInput, SegmentedControl, Stack, Text, TextInput, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { collection, deleteDoc, doc, setDoc } from "firebase/firestore";
import { useFirestore } from "../../helpers/firebaseContext";
import { useLiveCollection } from "./useLive";
import { CtcDynamicFieldDef } from "../../types";

const TYPES: { value: CtcDynamicFieldDef["type"]; label: string }[] = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "bool", label: "Yes / No" },
];
const typeLabel = (type: CtcDynamicFieldDef["type"]) => TYPES.find((t) => t.value === type)?.label ?? "Text (older field)";

type Draft = { id?: string; name_en: string; name_ar: string; type: CtcDynamicFieldDef["type"]; required: boolean; order: number | "" };

/**
 * Back office → Customer: extra fields for this customer's orders (ctc_customers/{id}/dynamic_fields), shown on
 * New purchase order and the order page. Saved here straight away, apart from the customer's Save. Orders keep
 * the labels they were saved with, so renaming or deleting a field doesn't change existing orders.
 */
export function CtcDynamicFieldsPanel({ customerId }: { customerId?: string }) {
    const db = useFirestore();
    const fields = useLiveCollection<CtcDynamicFieldDef>(customerId ? `ctc_customers/${customerId}/dynamic_fields` : null).data;
    const [draft, setDraft] = useState<Draft | null>(null);
    const [busy, setBusy] = useState(false);
    const sorted = [...(fields ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (!customerId) {
        return (
            <Card withBorder padding="md">
                <Text fw={600} mb={2}>Dynamic fields</Text>
                <Text size="xs" c="dimmed">Save the customer first, then add the extra fields its orders need.</Text>
            </Card>
        );
    }

    const nameError = (() => {
        if (!draft) return undefined;
        const name = draft.name_en.trim().toLowerCase();
        if (!name) return "Name is required";
        return sorted.some((f) => f.id !== draft.id && f.name_en.trim().toLowerCase() === name) ? "Another field has this name" : undefined;
    })();

    const save = async () => {
        if (!draft || nameError) return;
        setBusy(true);
        try {
            const ref = draft.id ? doc(db, `ctc_customers/${customerId}/dynamic_fields`, draft.id) : doc(collection(db, `ctc_customers/${customerId}/dynamic_fields`));
            const data: Omit<CtcDynamicFieldDef, "id"> = {
                name_en: draft.name_en.trim(),
                name_ar: draft.name_ar.trim(), // not edited here (the portal is English); kept for older fields
                type: draft.type,
                required: draft.type === "bool" ? false : draft.required, // Yes / No is always answered
                order: draft.order === "" ? sorted.length + 1 : Number(draft.order),
            };
            await setDoc(ref, data);
            notifications.show({ color: "green", message: `Field "${data.name_en}" saved` });
            setDraft(null);
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message || "Save failed" });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (f: CtcDynamicFieldDef) => {
        if (!window.confirm(`Delete the field "${f.name_en}"?\n\nNew orders won't ask for it. Orders that already have it keep showing it.`)) return;
        try {
            await deleteDoc(doc(db, `ctc_customers/${customerId}/dynamic_fields`, f.id!));
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message || "Delete failed" });
        }
    };

    return (
        <Card withBorder padding="md">
            <Group justify="space-between" mb={2}>
                <Text fw={600}>Dynamic fields</Text>
                {!draft && (
                    <Button size="compact-sm" variant="light" leftSection={<IconPlus size={14} />}
                        onClick={() => setDraft({ name_en: "", name_ar: "", type: "text", required: false, order: sorted.length + 1 })}>
                        Add field
                    </Button>
                )}
            </Group>
            <Text size="xs" c="dimmed" mb="sm">Extra fields on this customer's New purchase order, shown on the order page and Attachment 1. Saved straight away.</Text>

            <Stack gap={6}>
                {sorted.length === 0 && !draft && <Text size="sm" c="dimmed">No dynamic fields.</Text>}
                {sorted.map((f) => (
                    <Group key={f.id} justify="space-between" wrap="nowrap" gap="xs">
                        <div style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>{f.name_en}</Text>
                            <Group gap={4}>
                                <Badge size="xs" variant="light">{typeLabel(f.type)}</Badge>
                                {f.required && f.type !== "bool" && <Badge size="xs" variant="light" color="red">Required</Badge>}
                            </Group>
                        </div>
                        <Group gap={4} wrap="nowrap">
                            <Tooltip label="Edit">
                                <ActionIcon variant="subtle" onClick={() => setDraft({ id: f.id, name_en: f.name_en, name_ar: f.name_ar ?? "", type: f.type, required: !!f.required, order: f.order ?? "" })} aria-label={`Edit ${f.name_en}`}>
                                    <IconPencil size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                                <ActionIcon variant="subtle" color="red" onClick={() => remove(f)} aria-label={`Delete ${f.name_en}`}>
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </Group>
                ))}
            </Stack>

            {draft && (
                <Stack gap="xs" mt="md" p="sm" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: 8 }}>
                    <Text size="sm" fw={600}>{draft.id ? "Edit field" : "New field"}</Text>
                    <TextInput label="Name" required value={draft.name_en} error={draft.name_en ? nameError : undefined} onChange={(e) => setDraft({ ...draft, name_en: e.currentTarget.value })} />
                    <SegmentedControl size="xs" data={TYPES} value={TYPES.some((t) => t.value === draft.type) ? draft.type : "text"} onChange={(v) => setDraft({ ...draft, type: v as CtcDynamicFieldDef["type"] })} />
                    <Group grow align="flex-end">
                        <NumberInput label="Order" description="Smaller shows first" min={0} allowDecimal={false} value={draft.order} onChange={(v) => setDraft({ ...draft, order: v === "" ? "" : Number(v) })} />
                        <Checkbox label="Required" disabled={draft.type === "bool"} checked={draft.type !== "bool" && draft.required} onChange={(e) => setDraft({ ...draft, required: e.currentTarget.checked })} mb={8} />
                    </Group>
                    {draft.type === "bool" && <Text size="xs" c="dimmed">Yes / No is always answered (off = No).</Text>}
                    <Group justify="flex-end" gap="xs">
                        <Button size="compact-sm" variant="default" onClick={() => setDraft(null)} disabled={busy}>Cancel</Button>
                        <Button size="compact-sm" onClick={save} loading={busy} disabled={!!nameError}>Save field</Button>
                    </Group>
                </Stack>
            )}
        </Card>
    );
}
